import * as React from "react"
import { Link } from "react-router-dom"

import { cn } from "@/lib/utils"
import { toneClass } from "@/lib/status"
import { formatNumber } from "@/lib/format"

/*
  MatrixGrid — rows × columns of counts / percentages on a colour scale, or status cells, with click-through.
  For 09 #7 (buildings × tasks, % done), 13 #10 (floors × disciplines, drawing status), 16 #9 (projects × stages:
  document count / Due / Missing), 12 #10 and others.

    <MatrixGrid
      ariaLabel="Devices checked by building and task" rowHeader="Building"
      rows={buildings.map((b) => ({ key: b.id, label: b.name }))}
      columns={tasks.map((t) => ({ key: t.id, label: t.short, title: t.name }))}
      getCell={(row, col) => ({ value: pct[row.key]?.[col.key] })}          // numeric → colour scale
      scale={{ min: 0, max: 100 }} formatValue={(v) => `${v}`} legend
      onCellClick={(row, col) => openFiltered(row.key, col.key)} />

    // categorical cells (status language): return a tone and a label
    getCell={(row, col) => missing ? { tone: 'red', label: 'Missing' } : due ? { tone: 'amber', label: 'Due' }
                         : count ? { value: count, tone: 'navy' } : { empty: true, title: 'Not reached yet' }}

  Cell: { value (number), label (text shown instead of the value), tone (status tone → tinted cell, no scale),
  title (tooltip / accessible text), empty (grey dot), disabled (not clickable) } or null (empty).
  Numeric cells use 6 steps of navy (`.ui-heat[data-step]` in index.css, readable in light and dark).
  Keyboard: one Tab stop; arrow keys move between cells, Home / End jump within the row, Enter / Space opens.
  Rows: { key, label, sublabel, name (plain text for tooltips / screen readers; defaults to label when it is text),
  href (the row label becomes a navy link, e.g. to the project), linkTitle, linkTestId }.
  Props: rows [{ key, label, sublabel, name, href }], columns [{ key, label, title }], getCell, scale ({ min 0, max 100 }),
  formatValue, onCellClick(row, column, cell), rowHeader, legend (true | node), legendLabels ([low, high]),
  cellWidth (64), dense, ariaLabel, className, testId ('matrix').
*/
const STEPS = 6

export function heatStep(value, { min = 0, max = 100 } = {}) {
  const number = Number(value)
  if (value === null || value === undefined || value === "" || !Number.isFinite(number)) return 0
  if (max <= min) return number > min ? STEPS - 1 : 0
  const ratio = (number - min) / (max - min)
  if (ratio <= 0) return 0
  if (ratio >= 1) return STEPS - 1
  return Math.min(STEPS - 2, 1 + Math.floor(ratio * (STEPS - 2)))
}

function cellText(cell, formatValue) {
  if (!cell || cell.empty) return ""
  if (cell.label !== undefined && cell.label !== null) return String(cell.label)
  if (cell.value === undefined || cell.value === null || cell.value === "") return ""
  return formatValue ? formatValue(cell.value) : formatNumber(cell.value, { maxDecimals: 0 })
}

function MatrixGrid({
  rows,
  columns,
  getCell,
  scale = { min: 0, max: 100 },
  formatValue,
  onCellClick,
  rowHeader = "",
  legend = false,
  legendLabels,
  cellWidth = 64,
  dense = false,
  ariaLabel = "Matrix",
  className,
  testId = "matrix",
}) {
  const [active, setActive] = React.useState({ r: 0, c: 0 })
  const refs = React.useRef({})
  const clickable = typeof onCellClick === "function"
  const height = dense ? 26 : 32
  // The remembered cell can fall outside the grid when a filter shrinks it; clamp it so exactly one cell keeps
  // tabIndex 0 and the grid never drops out of the Tab order.
  const activeR = Math.max(0, Math.min(active.r, rows.length - 1))
  const activeC = Math.max(0, Math.min(active.c, columns.length - 1))

  const focusCell = (r, c) => {
    const nextR = Math.max(0, Math.min(rows.length - 1, r))
    const nextC = Math.max(0, Math.min(columns.length - 1, c))
    setActive({ r: nextR, c: nextC })
    refs.current[`${nextR}:${nextC}`]?.focus()
  }

  const onKeyDown = (event, r, c, cell) => {
    const moves = { ArrowRight: [0, 1], ArrowLeft: [0, -1], ArrowDown: [1, 0], ArrowUp: [-1, 0] }
    if (moves[event.key]) {
      event.preventDefault()
      focusCell(r + moves[event.key][0], c + moves[event.key][1])
    } else if (event.key === "Home") {
      event.preventDefault()
      focusCell(event.ctrlKey ? 0 : r, 0)
    } else if (event.key === "End") {
      event.preventDefault()
      focusCell(event.ctrlKey ? rows.length - 1 : r, columns.length - 1)
    } else if ((event.key === "Enter" || event.key === " ") && clickable && !cell?.disabled) {
      event.preventDefault()
      onCellClick(rows[r], columns[c], cell)
    }
  }

  return (
    <div className={cn("w-full min-w-0 [contain:inline-size]", className)} data-testid={testId}>
      <div className="overflow-x-auto">
        <table role="grid" aria-label={ariaLabel} className="border-separate border-spacing-[3px] text-[12px]">
          <thead>
            <tr>
              <th scope="col" className="sticky left-0 z-[1] bg-surface px-2 text-left align-bottom text-[11.5px] font-semibold text-ink-3">
                {rowHeader}
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  title={column.title || undefined}
                  className="px-1 pb-1 text-center align-bottom text-[11.5px] font-semibold leading-tight text-ink-2"
                  style={{ minWidth: cellWidth, maxWidth: cellWidth * 1.6 }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={row.key}>
                <th scope="row" className="sticky left-0 z-[1] max-w-[220px] bg-surface pr-3 text-left font-medium text-ink">
                  {row.href ? (
                    <span className="block truncate text-[12.5px]">
                      <Link
                        to={row.href}
                        className="rounded-[3px] font-semibold text-brand-ink hover:underline focus-visible:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        title={row.linkTitle || undefined}
                        data-testid={row.linkTestId || undefined}
                      >
                        {row.label}
                      </Link>
                    </span>
                  ) : (
                    <span className="block truncate text-[12.5px]">{row.label}</span>
                  )}
                  {row.sublabel ? <span className="block truncate text-[11px] font-normal text-ink-3">{row.sublabel}</span> : null}
                </th>
                {columns.map((column, c) => {
                  const cell = getCell(row, column) || null
                  const text = cellText(cell, formatValue)
                  const toned = cell?.tone && !cell.empty
                  const step = toned || !cell || cell.empty ? null : heatStep(cell.value, scale)
                  const isActive = activeR === r && activeC === c
                  const rowName = row.name ?? (typeof row.label === "string" || typeof row.label === "number" ? row.label : "")
                  const description = cell?.title || `${rowName} · ${column.title || column.label}: ${text || "nothing"}`
                  const content = cell?.empty || (!cell && !text) ? (
                    <span className="mx-auto block h-1.5 w-1.5 rounded-full bg-[color:var(--status-gray)]" aria-hidden="true" />
                  ) : text
                  const cellClass = cn(
                    "flex w-full items-center justify-center rounded-[6px] px-1 font-bold tabular-nums outline-none",
                    toned ? cn(toneClass(cell.tone), "bg-[color:var(--tone-bg)] text-[color:var(--tone-ink)]") : step !== null ? "ui-heat" : "bg-surface-2 text-ink-3",
                    clickable && !cell?.disabled && "cursor-pointer hover:ring-2 hover:ring-[color:var(--brand-soft-border)]",
                    "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  )
                  return (
                    <td key={column.key} role="gridcell" className="p-0" style={{ minWidth: cellWidth }}>
                      <div
                        ref={(node) => {
                          refs.current[`${r}:${c}`] = node
                        }}
                        role={clickable && !cell?.disabled ? "button" : undefined}
                        tabIndex={isActive ? 0 : -1}
                        aria-label={description}
                        title={description}
                        data-step={step ?? undefined}
                        data-testid={`${testId}-cell-${row.key}-${column.key}`}
                        className={cellClass}
                        style={{ height }}
                        onFocus={() => setActive({ r, c })}
                        onKeyDown={(event) => onKeyDown(event, r, c, cell)}
                        onClick={clickable && !cell?.disabled ? () => onCellClick(row, column, cell) : undefined}
                      >
                        {content}
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {legend ? (
        typeof legend === "boolean" ? (
          <div className="mt-2 flex items-center gap-1.5 text-[11.5px] text-ink-3" data-testid={`${testId}-legend`}>
            <span>{legendLabels?.[0] ?? formatNumber(scale.min ?? 0, { maxDecimals: 0 })}</span>
            {Array.from({ length: STEPS }, (_, step) => (
              <span key={step} className="ui-heat inline-block h-3 w-4 rounded-[3px]" data-step={step} aria-hidden="true" />
            ))}
            <span>{legendLabels?.[1] ?? formatNumber(scale.max ?? 100, { maxDecimals: 0 })}</span>
          </div>
        ) : legend
      ) : null}
    </div>
  )
}

export { MatrixGrid }
